"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { CreateProspectPayload, CustomField, customFieldLabel, PHONE_TYPES, PhoneType, Prospect } from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { usePinnedDialogStore } from "@/stores/use-pinned-dialog-store"
import { useProspectStore } from "@/stores/use-prospect-store"
import { isValidPhone } from "@/utils/phone"
import { zodResolver } from "@hookform/resolvers/zod"
import { BookSelectField, TextField } from "@ui/forms"
import { DateInput } from "@ui/forms/inputs/date-input"
import { TextInput } from "@ui/forms/inputs/text-input"
import { PhoneInput } from "@ui/phone-input"
import {
  Button,
  Checkbox,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ui/shad"
import { PictureInPicture2, X } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import * as z from "zod"

import { ALL_BOOKS_SQID, isAllBooksId } from "@/lib/all-books"
import { useFetchCustomFields } from "@/hooks/api/custom_fields"
import { useFetchPipelines } from "@/hooks/api/pipelines"
import { useCreateProspect } from "@/hooks/api/prospects"
import { useFetchTags } from "@/hooks/api/tags"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useSingleFlight } from "@/hooks/use-single-flight"
import { CustomFieldFormInput } from "@/components/custom-fields"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"
import { getFirstStageId } from "@/components/opportunities/create-opportunity-dialog"
import { UserSelectorField } from "@/components/opportunities/create/user-selector-field"
import { OpportunityLabelSelector } from "@/components/opportunities/opportunity-label-selector"

const createFormSchema = (requireBookId: boolean) =>
  z
    .object({
      firstName: z.string().optional().default(""),
      lastName: z.string().optional().default(""),
      email: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
      phone: z
        .string()
        .refine((val) => !val || isValidPhone(val), { message: "Please enter a valid phone number" })
        .optional()
        .or(z.literal("")),
      phoneExtension: z.string().regex(/^\d*$/, "Extension must contain only numbers").optional().default(""),
      phoneType: z.enum(PHONE_TYPES),
      bookId: requireBookId ? z.string().min(1, "Book is required") : z.string().optional(),
      createOpportunity: z.boolean().default(false),
      opportunityPipelineId: z.string().default(""),
      opportunityStageId: z.string().default(""),
      opportunityValue: z.number().min(0, "Value must be 0 or greater").nullable(),
      opportunityTargetCloseDate: z.string().nullable(),
      opportunityOwnerId: z.string().nullable(),
    })
    .refine((data) => data.firstName?.trim() || data.email?.trim(), {
      message: "Either first name or email is required",
      path: ["firstName"],
    })
    .superRefine((data, ctx) => {
      if (!data.createOpportunity) return

      if (!data.opportunityPipelineId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pipeline is required", path: ["opportunityPipelineId"] })
      }
      if (!data.opportunityStageId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Stage is required", path: ["opportunityStageId"] })
      }
    })

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

export function ProspectForm({
  id = "",
  defaultEmail = "",
  defaultPhone = "",
  defaultFirstName = "",
  defaultLastName = "",
  onCreated,
}: {
  id?: string
  defaultEmail?: string
  defaultPhone?: string
  defaultFirstName?: string
  defaultLastName?: string
  onCreated?: (prospect: Prospect) => void
}) {
  const dialogStore = useGlobalDialogStore()
  const { pinnedDialogs, unpinDialog, closeDialog } = usePinnedDialogStore()
  const [draftId, setDraftId] = useState(id)
  const { drafts, createDraft, updateDraft, removeDraft } = useProspectStore()
  const createProspectMutation = useCreateProspect()
  const { begin: beginCreate, end: endCreate } = useSingleFlight()
  const isPinned = pinnedDialogs.some((dialog) => dialog.id === draftId)
  const [createMore, setCreateMore] = useState(false)
  const firstNameInputRef = useRef<HTMLInputElement | null>(null)
  const autoFocusedDraftIdRef = useRef<string | null>(null)
  const router = useRouter()
  const bookId = useBookId()
  const showBookSelector = isAllBooksId(bookId)
  const formSchema = useMemo(() => createFormSchema(showBookSelector), [showBookSelector])

  useEffect(() => {
    if (!draftId) {
      const newDraftId = createDraft({
        firstName: defaultFirstName || "",
        lastName: defaultLastName || "",
        email: defaultEmail || "",
        phone: defaultPhone || "",
      })
      setDraftId(newDraftId)
    }
  }, [draftId, createDraft, defaultFirstName, defaultLastName, defaultEmail, defaultPhone])

  const draft = draftId ? drafts[draftId] : null

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: draft?.firstName || defaultFirstName || "",
      lastName: draft?.lastName || defaultLastName || "",
      email: draft?.email || defaultEmail || "",
      phone: draft?.phone || defaultPhone || "",
      phoneExtension: draft?.phoneExtension || "",
      phoneType: draft?.phoneType || "mobile",
      bookId: draft?.bookId || "",
      createOpportunity: draft?.createOpportunity ?? false,
      opportunityPipelineId: draft?.opportunityPipelineId || "",
      opportunityStageId: draft?.opportunityStageId || "",
      opportunityValue: draft?.opportunityValue ?? null,
      opportunityTargetCloseDate: draft?.opportunityTargetCloseDate ?? null,
      opportunityOwnerId: draft?.opportunityOwnerId ?? null,
    },
  })

  const createOpportunity = form.watch("createOpportunity")
  const selectedBookId = form.watch("bookId")
  const selectedPipelineId = form.watch("opportunityPipelineId")
  const opportunityBookId = showBookSelector ? selectedBookId || undefined : undefined
  const canLoadBookData = !showBookSelector || !!opportunityBookId
  const { data: pipelinesData, isLoading: isLoadingPipelines } = useFetchPipelines(opportunityBookId, canLoadBookData)
  const prospectPipelines = useMemo(
    () => pipelinesData?.pipelines.filter((pipeline) => pipeline.audience_types.includes("prospect")) ?? [],
    [pipelinesData?.pipelines]
  )
  const selectedPipeline = prospectPipelines.find((pipeline) => pipeline.id === selectedPipelineId)
  const activeStages = selectedPipeline?.pipeline_stages ?? []
  const showPipelineSelector = prospectPipelines.length > 1
  const { data: currentUser } = useFetchCurrentUser()
  const { data: customFieldsData } = useFetchCustomFields("Opportunity", { enabled: createOpportunity })
  const { data: tagsData } = useFetchTags("opportunity", {
    enabled: createOpportunity && canLoadBookData,
    bookIdOverride: opportunityBookId,
  })
  const customFields = useMemo(() => customFieldsData?.custom_fields ?? [], [customFieldsData?.custom_fields])
  const hasLabels = (tagsData?.tags?.length ?? 0) > 0
  const customFieldValues = draft?.opportunityCustomFieldValues ?? {}
  const opportunityLabel = draft?.opportunityLabel ?? null

  useEffect(() => {
    if (!draftId || !draft) return

    form.reset({
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      phone: draft.phone ?? "",
      phoneExtension: draft.phoneExtension ?? "",
      phoneType: draft.phoneType,
      bookId: draft.bookId ?? "",
      createOpportunity: draft.createOpportunity,
      opportunityPipelineId: draft.opportunityPipelineId,
      opportunityStageId: draft.opportunityStageId,
      opportunityValue: draft.opportunityValue,
      opportunityTargetCloseDate: draft.opportunityTargetCloseDate,
      opportunityOwnerId: draft.opportunityOwnerId,
    })

    setCreateMore(draft.createMore ?? false)
  }, [draftId, draft, form])

  useEffect(() => {
    if (!draftId || !currentUser?.id || form.getValues("opportunityOwnerId")) return

    form.setValue("opportunityOwnerId", currentUser.id)
    updateDraft(draftId, { opportunityOwnerId: currentUser.id })
  }, [currentUser?.id, draftId, form, updateDraft])

  useEffect(() => {
    if (!draftId || !pipelinesData) return

    const currentPipelineId = form.getValues("opportunityPipelineId")
    const currentPipeline = prospectPipelines.find((pipeline) => pipeline.id === currentPipelineId)
    const defaultPipeline =
      currentPipeline ??
      prospectPipelines.find((pipeline) => pipeline.default_audience_types.includes("prospect")) ??
      prospectPipelines.find((pipeline) => pipelinesData.favorite_pipeline_ids.includes(pipeline.id)) ??
      prospectPipelines[0]

    const nextPipelineId = defaultPipeline?.id ?? ""
    const currentStageId = form.getValues("opportunityStageId")
    const stageIsValid = defaultPipeline?.pipeline_stages.some((stage) => stage.id === currentStageId)
    const nextStageId = stageIsValid ? currentStageId : getFirstStageId(defaultPipeline?.pipeline_stages ?? [])

    form.setValue("opportunityPipelineId", nextPipelineId)
    form.setValue("opportunityStageId", nextStageId)
    updateDraft(draftId, { opportunityPipelineId: nextPipelineId, opportunityStageId: nextStageId })
  }, [draftId, form, pipelinesData, prospectPipelines, updateDraft])

  useEffect(() => {
    if (!draftId || !draft || autoFocusedDraftIdRef.current === draftId) return

    const focusFrame = window.requestAnimationFrame(() => {
      firstNameInputRef.current?.focus()
      autoFocusedDraftIdRef.current = draftId
    })

    return () => window.cancelAnimationFrame(focusFrame)
  }, [draftId, draft])

  const onValueChange = (values: Partial<FormValues>) => {
    if (!draftId) return
    updateDraft(draftId, values)
  }

  const handleCreateProspect = (values: FormValues, effectiveCreateMore?: boolean) => {
    if (!draftId) return
    // Guard the handler, not just the button — ⌘+Enter reaches this directly.
    if (createProspectMutation.isPending) return

    const shouldCreateMore = effectiveCreateMore ?? createMore
    const householdName = `${values.firstName} ${values.lastName}`.trim()

    const prospectData: CreateProspectPayload = {
      people_attributes: [
        {
          first_name: values.firstName,
          last_name: values.lastName,
          household_role: "head_of_household",
          email_addresses_attributes: values.email
            ? [
                {
                  email: values.email,
                  is_primary: true,
                },
              ]
            : undefined,
          phone_numbers_attributes: values.phone
            ? [
                {
                  unformatted_phone: values.phone,
                  extension: values.phoneExtension || undefined,
                  is_primary: true,
                  phone_type: values.phoneType,
                },
              ]
            : undefined,
        },
      ],
    }

    const opportunityData = values.createOpportunity
      ? {
          pipeline_id: values.opportunityPipelineId,
          pipeline_stage_id: values.opportunityStageId,
          name: householdName,
          value: values.opportunityValue ?? undefined,
          target_close_date: values.opportunityTargetCloseDate
            ? new Date(values.opportunityTargetCloseDate)
            : undefined,
          owner_id: values.opportunityOwnerId,
          ...(Object.keys(customFieldValues).length > 0 ? { custom_fields_attributes: customFieldValues } : {}),
          ...(opportunityLabel ? { tag_ids: [opportunityLabel.id] } : {}),
        }
      : undefined

    if (!beginCreate()) return

    createProspectMutation.mutate(
      { prospect: prospectData, opportunity: opportunityData, overrideBookId: values.bookId },
      {
        onSettled: endCreate,
        onSuccess: (data) => {
          const targetBookId = values.bookId ? ALL_BOOKS_SQID : bookId
          const prospectPath = `/${targetBookId}/prospects/${data.id}?secondary=details`

          if (onCreated) {
            onCreated(data)
          } else {
            toast.success(values.createOpportunity ? "Prospect and opportunity created" : "Prospect created")
          }

          if (shouldCreateMore) {
            const newDraftId = createNewDraft(shouldCreateMore)
            switchToNewDraft(newDraftId, draftId)
          } else {
            if (!onCreated) {
              router.push(prospectPath)
            }
            resetForm()
          }
        },
      }
    )
  }

  const resetForm = () => {
    if (draftId) {
      removeDraft(draftId)
    }

    if (isPinned) {
      closeDialog(draftId)
    } else {
      dialogStore.closeDialog()
    }
  }

  const createNewDraft = (preserveCreateMore: boolean) => {
    const newDraftId = createDraft()

    updateDraft(newDraftId, {
      createMore: preserveCreateMore,
    })

    return newDraftId
  }

  const switchToNewDraft = (newDraftId: string, oldDraftId: string) => {
    setDraftId(newDraftId)
    autoFocusedDraftIdRef.current = null

    if (isPinned) {
      const { updateDialogId } = usePinnedDialogStore.getState()
      updateDialogId(oldDraftId, newDraftId, "prospect")
    }

    removeDraft(oldDraftId)
  }

  const handlePin = () => {
    if (!draftId) return

    if (isPinned) {
      unpinDialog(draftId)
    } else {
      dialogStore.pinDialog(draftId, "prospect", bookId)
    }
  }

  const handleClose = () => {
    if (!draftId) return

    if (isPinned) {
      closeDialog(draftId)
      resetForm()
    } else {
      dialogStore.closeDialog()
    }
  }

  const handleCreateMoreChange = (checked: boolean) => {
    if (!draftId) return
    setCreateMore(checked)
    updateDraft(draftId, { createMore: checked })
  }

  if (!draft) return null

  return (
    <CustomDialogContent title="Create new prospect">
      <div className="relative flex max-h-[calc(100vh-8rem)] w-full flex-col">
        <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b pr-3 pl-6">
          <div className="font-semibold">Create new prospect</div>
          <div className="flex items-center justify-end gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="iconSm" variant={isPinned ? "secondary" : "ghost"} onClick={handlePin}>
                  <PictureInPicture2 className="text-icon hover:text-icon-hover" />
                  <span className="sr-only">{isPinned ? "Unpin" : "Pin to screen"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isPinned ? "Unpin" : "Pin to screen"}</TooltipContent>
            </Tooltip>
            <Button size="iconSm" variant="ghost" onClick={handleClose}>
              <X className="text-icon hover:text-icon-hover" />
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => handleCreateProspect(values, createMore))}
            onKeyDown={(e) => {
              if (e.metaKey && e.key === "Enter") {
                e.preventDefault()
                const nextCreateMore = e.shiftKey ? !createMore : undefined
                if (nextCreateMore !== undefined) {
                  handleCreateMoreChange(nextCreateMore)
                }
                form.handleSubmit((values) => handleCreateProspect(values, nextCreateMore))()
              }
            }}
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
          >
            {showBookSelector && (
              <FormField
                control={form.control}
                name="bookId"
                render={({ field }) => (
                  <BookSelectField<FormValues>
                    id="bookId"
                    field={{
                      ...field,
                      onChange: (value: string) => {
                        field.onChange(value)
                        form.setValue("opportunityPipelineId", "")
                        form.setValue("opportunityStageId", "")
                        if (draftId) updateDraft(draftId, { opportunityLabel: null })
                        onValueChange({ bookId: value, opportunityPipelineId: "", opportunityStageId: "" })
                      },
                    }}
                  />
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="firstName"
                render={({ field }) => (
                  <TextField
                    id="firstName"
                    label="First name"
                    placeholder="First name"
                    autoComplete="off"
                    field={{
                      ...field,
                      ref: (node) => {
                        field.ref(node)
                        firstNameInputRef.current = node
                      },
                      onChange: (e) => {
                        field.onChange(e)
                        onValueChange({ firstName: e.target.value })
                      },
                    }}
                    error={!!form.formState.errors.firstName?.message}
                  />
                )}
              />

              <FormField
                name="lastName"
                render={({ field }) => (
                  <TextField
                    id="lastName"
                    label="Last name"
                    placeholder="Last name"
                    autoComplete="off"
                    field={{
                      ...field,
                      onChange: (e) => {
                        field.onChange(e)
                        onValueChange({ lastName: e.target.value })
                      },
                    }}
                    error={!!form.formState.errors.lastName?.message}
                  />
                )}
              />
            </div>

            <FormField
              name="email"
              render={({ field }) => (
                <TextField
                  id="email"
                  label="Email"
                  placeholder="Email address"
                  autoComplete="off"
                  field={{
                    ...field,
                    onChange: (e) => {
                      field.onChange(e)
                      onValueChange({ email: e.target.value })
                    },
                  }}
                  error={!!form.formState.errors.email?.message}
                />
              )}
            />

            <div className="grid grid-cols-3 gap-2">
              <FormField
                name="phone"
                render={({ field }) => (
                  <div className="col-span-2 space-y-1">
                    <Label htmlFor="phone">Phone</Label>
                    <PhoneInput
                      value={field.value ?? ""}
                      onChange={(val) => {
                        field.onChange(val)
                        onValueChange({ phone: val })
                      }}
                      extension={form.watch("phoneExtension") ?? ""}
                      onExtensionChange={(value) => {
                        form.setValue("phoneExtension", value, { shouldValidate: true })
                        onValueChange({ phoneExtension: value })
                      }}
                    />
                    {form.formState.errors.phone?.message ? (
                      <span className="text-destructive text-xs">{form.formState.errors.phone.message}</span>
                    ) : null}
                  </div>
                )}
              />
              <FormField
                name="phoneType"
                render={({ field }) => (
                  <div className="space-y-1">
                    <Label>Type</Label>
                    <Select
                      value={field.value}
                      onValueChange={(value: PhoneType) => {
                        field.onChange(value)
                        onValueChange({ phoneType: value })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHONE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="createOpportunity"
              render={({ field }) => (
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="createOpportunity"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      const nextValue = checked === true
                      field.onChange(nextValue)
                      onValueChange({ createOpportunity: nextValue })
                    }}
                  />
                  <Label htmlFor="createOpportunity" className="cursor-pointer">
                    Also create an opportunity
                  </Label>
                </div>
              )}
            />

            {createOpportunity && (
              <div className="bg-muted/40 [&_input]:bg-background [&_[role=combobox]]:bg-background grid grid-cols-2 gap-4 rounded-lg border px-4 pt-3 pb-4">
                {showPipelineSelector && (
                  <FormField
                    control={form.control}
                    name="opportunityPipelineId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pipeline</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            const pipeline = prospectPipelines.find((candidate) => candidate.id === value)
                            const stageId = getFirstStageId(pipeline?.pipeline_stages ?? [])
                            field.onChange(value)
                            form.setValue("opportunityStageId", stageId, { shouldValidate: true })
                            onValueChange({ opportunityPipelineId: value, opportunityStageId: stageId })
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a pipeline" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {prospectPipelines.map((pipeline) => (
                              <SelectItem key={pipeline.id} value={pipeline.id}>
                                {pipeline.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {!isLoadingPipelines && canLoadBookData && prospectPipelines.length === 0 && (
                  <div className="text-muted-foreground col-span-2 text-sm">
                    No pipeline configured for prospects. Add a prospect pipeline before creating an opportunity.
                  </div>
                )}

                {!canLoadBookData && (
                  <div className="text-muted-foreground col-span-2 text-sm">
                    Select a book to load its opportunity pipelines.
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="opportunityStageId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value)
                          onValueChange({ opportunityStageId: value })
                        }}
                        disabled={activeStages.length === 0}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a stage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeStages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opportunityValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Value</FormLabel>
                      <FormControl>
                        <TextInput
                          {...field}
                          id="opportunityValue"
                          type="number"
                          placeholder="0"
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const value = event.target.value ? Number(event.target.value) : null
                            field.onChange(value)
                            onValueChange({ opportunityValue: value })
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opportunityTargetCloseDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target close date</FormLabel>
                      <FormControl>
                        <DateInput
                          id="opportunityTargetCloseDate"
                          value={field.value ? new Date(field.value) : undefined}
                          onChange={(date) => {
                            const value = date?.toISOString() ?? null
                            field.onChange(value)
                            onValueChange({ opportunityTargetCloseDate: value })
                          }}
                          placeholder="Select date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opportunityOwnerId"
                  render={({ field }) => (
                    <UserSelectorField
                      field={{
                        ...field,
                        onChange: (value) => {
                          field.onChange(value)
                          onValueChange({ opportunityOwnerId: value })
                        },
                      }}
                      bookIdOverride={opportunityBookId}
                    />
                  )}
                />

                {hasLabels && (
                  <FormItem>
                    <FormLabel>Label</FormLabel>
                    <FormControl>
                      <OpportunityLabelSelector
                        label={opportunityLabel}
                        onLabelChange={(label) => {
                          if (draftId) updateDraft(draftId, { opportunityLabel: label })
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}

                {customFields.map((customField: CustomField) => (
                  <FormItem key={customField.id}>
                    <FormLabel>{customFieldLabel(customField)}</FormLabel>
                    <FormControl>
                      <CustomFieldFormInput
                        customField={customField}
                        value={customFieldValues[customField.field_key]}
                        onSave={(value) => {
                          if (!draftId) return
                          updateDraft(draftId, {
                            opportunityCustomFieldValues: {
                              ...customFieldValues,
                              [customField.field_key]: value,
                            },
                          })
                        }}
                        isLoading={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                ))}
              </div>
            )}
          </form>
        </Form>

        <DialogFooter className="round flex w-full shrink-0 items-center justify-end p-3">
          <div className="-my-1! flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={createMore} onCheckedChange={handleCreateMoreChange} />
              <span className="text-foreground text-xs">Create more</span>
            </div>
            <Button
              onClick={() => form.handleSubmit((values) => handleCreateProspect(values, createMore))()}
              disabled={createProspectMutation.isPending}
            >
              Create
            </Button>
          </div>
        </DialogFooter>
      </div>
    </CustomDialogContent>
  )
}

export function useCreateProspectDialog() {
  const { openDialog, closeDialog } = useGlobalDialogStore()

  const open = useCallback(
    (
      options: {
        defaultEmail?: string
        defaultPhone?: string
        defaultFirstName?: string
        defaultLastName?: string
        onCreated?: (prospect: Prospect) => void
      } = {}
    ) => {
      const { defaultEmail = "", defaultPhone = "", defaultFirstName = "", defaultLastName = "", onCreated } = options

      openDialog({
        variant: "custom",
        content: (
          <ProspectForm
            defaultEmail={defaultEmail}
            defaultPhone={defaultPhone}
            defaultFirstName={defaultFirstName}
            defaultLastName={defaultLastName}
            onCreated={onCreated}
          />
        ),
        dialogContentClasses: "max-h-[calc(100vh-8rem)] max-w-(--breakpoint-sm)",
      })
    },
    [openDialog]
  )

  return {
    open,
    close: () => {
      closeDialog()
    },
  }
}
